import { differenceInCalendarDays, isAfter } from 'date-fns';
import { format } from 'date-fns/format';
import { isBefore } from 'date-fns/isBefore';

import {
  CalendarRoomRow,
  CalendarRoomRowApiModel,
  RoomAvailabilityApiResponseModel,
  RoomCalendarEventsModel,
  RoomHistoryApiResponseModel,
  RoomHistoryChangesModel,
  RoomHistoryEntryModel,
  RoomSettingsApiRequestModel,
} from '@bookly/shared';

import { utcDateToTimezoneDisplayDate } from '../../core';

export function roomCalendarEventsConverter(
  roomAvailability: RoomAvailabilityApiResponseModel
): RoomCalendarEventsModel[] {
  return roomAvailability.ranges.map(roomEvent => ({
    start: new Date(roomEvent.start),
    end: new Date(roomEvent.end),
    title: roomEvent.userFullName ?? roomEvent.organizer,
    allDay: !roomEvent.end.includes('T'),
    meta: {
      timeStart: format(roomEvent.start, 'HH:mm'),
      timeEnd: format(roomEvent.end, 'HH:mm'),
      status: roomEvent.status ?? 'unknown',
      type: roomEvent.type ?? 'unknown',
      eventName:
        roomEvent.eventName ??
        (!roomEvent.title || roomEvent.title.length === 0
          ? 'Busy'
          : roomEvent.title),
      userFullName: roomEvent.userFullName ?? roomEvent.organizer,
      bookingId: roomEvent.bookingId ? +roomEvent.bookingId : null,
    },
  }));
}

/**
 * Converts the user calendar-items response (rooms already grouped with their
 * events) into the {@link CalendarRoomRow} rows the week calendar renders.
 * Events are clipped to the requested [dateFrom, dateTo] range so multi-day
 * bars that start before / end after the visible week span correctly.
 */
export function calendarItemsConverter(
  response: CalendarRoomRowApiModel[],
  dateFrom: string,
  dateTo: string
): CalendarRoomRow[] {
  const visibleStart = new Date(dateFrom);
  const visibleEnd = new Date(dateTo);

  return response.map(room => {
    const siteTimeZone = room.siteTimeZone ?? null;
    const toSiteDate = (value: string) => {
      const parsed = new Date(value);
      return siteTimeZone
        ? utcDateToTimezoneDisplayDate(parsed, siteTimeZone)
        : parsed;
    };

    const events = room.events.map(event => {
      const startTime = toSiteDate(event.startTime);
      const endTime = toSiteDate(event.endTime);
      const createTime = new Date(event.createTime);
      const calendarDateFrom = isBefore(startTime, visibleStart)
        ? visibleStart
        : startTime;
      const calendarDateTo = isAfter(endTime, visibleEnd)
        ? visibleEnd
        : endTime;

      return {
        ...event,
        startTime,
        endTime,
        createTime,
        calendarDateFrom,
        eventDurationInDays:
          differenceInCalendarDays(calendarDateTo, calendarDateFrom) + 1,
      };
    });

    return {
      roomDexId: room.roomDexId,
      roomName: room.roomName,
      siteName: room.siteName,
      buildingName: room.buildingName,
      floorName: room.floorName,
      capacity: room.capacity,
      siteTimeZone,
      isMultiDayEvent: events.some(
        event =>
          differenceInCalendarDays(event.endTime, event.startTime) + 1 > 1
      ),
      events,
    };
  });
}

/**
 * Converts the room-settings history response into the entries the
 * `bad-room-history` component renders. Mirrors the booking-history converter:
 * revisions are mapped as-is (with `updateDate` parsed to a `Date`) and a
 * synthetic "revision 0" snapshot is prepended from `initialManageSpace` so the
 * timeline starts with the room's original configuration.
 */
export function roomHistoryConverter(
  response: RoomHistoryApiResponseModel
): RoomHistoryEntryModel[] {
  const entries: RoomHistoryEntryModel[] = (response.revisions ?? []).map(
    revision => ({
      ...revision,
      updateDate: revision.updateDate ? new Date(revision.updateDate) : null,
    })
  );

  if (response.initialConfiguration) {
    const initial = response.initialConfiguration;
    entries.unshift({
      revisionNumber: 0,
      updatedBy: '',
      updateDate: null,
      changes: initialConfigurationToChanges(initial),
    });
  }

  return entries;
}

/**
 * Builds a `RoomHistoryChangesModel` from a room-settings snapshot where every
 * value is a leaf holding only `current`. Values are kept as their native type
 * (boolean, number, array, object) to match the change entries returned by the
 * diff endpoint.
 */
function initialConfigurationToChanges(
  initial: RoomSettingsApiRequestModel
): RoomHistoryChangesModel {
  const result: RoomHistoryChangesModel = {};
  const set = (key: string, value: unknown) => {
    if (value !== undefined && value !== null) {
      result[key] = { current: value };
    }
  };

  set('active', initial.active);
  set('description', initial.description);
  set('automaticApproval', initial.automaticApproval);
  set('defaultLayoutId', initial.defaultLayoutId);
  set('freezeDays', initial.freezeDays);
  set('generalQuestionsEnabled', initial.generalQuestionsEnabled);

  if (initial.services?.length) {
    set('services', initial.services);
  }
  if (initial.blockingTimes?.length) {
    set('blockingTimes', initial.blockingTimes);
  }
  if (initial.durationRange) {
    set('durationRange', initial.durationRange);
  }

  return result;
}
